/**
 * Bridges scene-locations to the `userData.partId` strings used by the
 * procedural model factories (acInductionMotorModel, gearboxModel, ...), so that an
 * event on a specific bearing can highlight just that section of the model
 * (e.g. the motor's drive end) instead of the whole component.
 *
 * Location data has no explicit part field — we derive it from the
 * location's name / description / type strings, which are user-facing labels
 * like "Motor Drive End" or "Pump Non-Drive End". Returns `null` when no
 * specific section applies (component-level event), which callers should
 * treat as a signal to tint the whole component.
 */

/**
 * `description` is a snake_case slug by platform convention, not prose —
 * Location.title humanizes it (`humanize(self.description)`) and the machine-info
 * formatter does `description.replace('_', ' ')`. It is therefore a legitimate
 * part signal and must keep contributing, otherwise part inference would break
 * on any payload served before `location_type_name` exists (older backend, or a
 * location with no type).
 *
 * But it is also a user-editable free-text column. Accept it only when it still
 * looks like a slug: a single lowercase snake_case token with no spaces. That
 * keeps every real slug working while stopping prose — "replaced during the fan
 * overhaul" on a drive-end bearing — from matching the rear-end pattern and
 * tinting the wrong bearing.
 */
const SLUG_RE = /^[a-z0-9]+(?:_[a-z0-9]+)*$/;

function slugLike(value) {
  return typeof value === 'string' && SLUG_RE.test(value.trim());
}

function normalizeText(location) {
  if (!location) return '';
  return [
    location.name,
    slugLike(location.description) ? location.description : null,
    location.location_type,
    location.location_type_name,
    location.locationType,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
    .replace(/[_-]+/g, ' ');
}

/**
 * Bodies whose meshes are tagged per SHAFT rather than per end: every part of the input
 * (high-speed) shaft is `drive_end` and every part of the output shaft is `rear_end`, because
 * the partId vocabulary has two values and these gearboxes have three shafts.
 *
 * Both screw compressors carry the same `input_shaft_free_end` style slugs but tag by end, so
 * membership here — not the location text — is what decides which reading applies.
 */
const SHAFT_TAGGED_MODEL_KEYS = {
  multi_stage_gearbox: true,
  multi_stage_gearbox_vertical: true,
};

/**
 * `modelKey` is the resolved registry key of the component that owns the location. It is
 * optional: callers that do not have it get the end-word reading, which is correct for every
 * body except the two in SHAFT_TAGGED_MODEL_KEYS.
 */
export function getLocationPartId(location, modelKey) {
  const text = normalizeText(location);
  if (!text) return null;
  // Order is load-bearing, and the three steps must stay in this sequence.
  //
  // 1. The explicit negations first. "non drive end" contains "drive", so if the
  //    drive-end test ran before this it would claim it.
  // 2. Then drive end. This has to beat the generic rear-end words below,
  //    because "Fan Drive End" is a drive end that merely mentions the fan —
  //    previously "fan" matched first and the alarm tinted the opposite bearing.
  // 3. Then the remaining rear-end words, which are only reached when the
  //    location did not identify itself as a drive end at all.
  // 0. On a body that tags its geometry per SHAFT, the shaft name answers the location and the
  //    end word does not. Only the two multi-stage gearboxes do this — see
  //    SHAFT_TAGGED_MODEL_KEYS. Both screw compressors use the same `X_shaft_Y_end` slugs but
  //    tag by END (+X drive, -X free), so this must NOT apply to them: the location text alone
  //    cannot tell the two schemes apart, which is why the model key has to be passed in.
  if (modelKey && SHAFT_TAGGED_MODEL_KEYS[modelKey]) {
    const shaft = /\b(input|idle|intermediate|output|main)\s+shaft\b/.exec(text);
    if (shaft) return shaft[1] === 'output' ? 'rear_end' : 'drive_end';
  }

  if (/\b(non drive|nde)\b/.test(text)) return 'rear_end';
  if (/\b(drive|de)\b/.test(text)) return 'drive_end';
  if (/\b(fan|rear|free)\b/.test(text)) return 'rear_end';
  if (/\bbushing/.test(text)) return 'bushings';
  if (/\bterminal/.test(text)) return 'terminal_box';
  // 4. Last of all, the gearbox vocabulary. A gearbox names its ends input and output rather
  //    than drive and free (`gearbox_input_end` / `gearbox_output_end`), so without these two
  //    nothing matched and a gearbox bearing alarm tinted the WHOLE gearbox instead of the
  //    end that raised it. Input is the end the motor couples to, hence the drive end — the
  //    same reading MountPointManager.getLocationEnd takes, and for the same reason.
  //
  //    Dead last so that every match above keeps the meaning it already had. That matters
  //    twice over: on a multi-stage gearbox input/output name the SHAFT rather than the end
  //    (`input_shaft_free_end` is the free end of the input shaft), and those locations carry
  //    their own drive/free word which step 2 or 3 has already claimed.
  if (/\binput\b/.test(text)) return 'drive_end';
  if (/\boutput\b/.test(text)) return 'rear_end';
  return null;
}

/**
 * Builds both location maps from sceneLocations in one pass, so callers share one source of
 * truth.
 *
 * Returns:
 *   locationToComponentMap — { [locationId]: componentId (string) }
 *   locationToPartMap      — { [locationId]: partId (string) | null }
 */
/**
 * componentId -> resolved registry key, which is what tells getLocationPartId whether a body
 * tags per shaft or per end. `resolve` is injected rather than imported so this module stays
 * free of the model registry.
 */
export function buildModelKeyByComponentId(sceneComponents, resolve) {
  const out = {};
  if (!resolve) return out;
  (sceneComponents || []).forEach((comp) => {
    if (comp && comp.id != null) out[String(comp.id)] = resolve(comp);
  });
  return out;
}

export function buildLocationMaps(sceneLocations, modelKeyByComponentId) {
  const locationToComponentMap = {};
  const locationToPartMap = {};
  (sceneLocations || []).forEach((loc) => {
    if (!loc || loc.id == null) return;
    const key = String(loc.id);
    const componentId = loc.component_id != null ? String(loc.component_id) : null;
    if (componentId) locationToComponentMap[key] = componentId;
    // The owning body's model key decides how a per-shaft location reads — see
    // SHAFT_TAGGED_MODEL_KEYS. Absent, every location falls back to the end-word reading.
    const modelKey = modelKeyByComponentId && componentId
      ? modelKeyByComponentId[componentId]
      : null;
    locationToPartMap[key] = getLocationPartId(loc, modelKey);
  });
  return { locationToComponentMap, locationToPartMap };
}
